import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, MenuItem, TextField,
  Button, CircularProgress, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Stack, Alert, Paper, Divider, LinearProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Tooltip, Tab, Tabs, InputAdornment, Stepper, Step, StepLabel,
  Accordion, AccordionSummary, AccordionDetails, Badge
} from '@mui/material';
import {
  School, Add, Edit, Delete, Search, Refresh,
  CheckCircle, Cancel, Schedule, Assignment, Person,
  ExpandMore, Download, Verified, VisibilityOff,
  HowToReg, EventNote, BarChart, OpenInNew, FileDownload
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { settingsAPI } from '../services/api';
import { exportCSV } from '../utils/export';
import axios from 'axios';

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api' });
api.interceptors.request.use(c => {
  const t = localStorage.getItem('sms_token');
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const STATUS_CFG = {
  draft:               { label: 'Draft',               color: 'default'  },
  submitted:           { label: 'Submitted',           color: 'info'     },
  under_review:        { label: 'Under Review',        color: 'warning'  },
  shortlisted:         { label: 'Shortlisted',         color: 'primary'  },
  interview_scheduled: { label: 'Interview Scheduled', color: 'secondary'},
  interviewed:         { label: 'Interviewed',         color: 'warning'  },
  accepted:            { label: 'Accepted',            color: 'success'  },
  rejected:            { label: 'Rejected',            color: 'error'    },
  withdrawn:           { label: 'Withdrawn',           color: 'default'  },
};
const INTAKE_STATUS = {
  draft:     { label: 'Draft',     color: 'default'  },
  open:      { label: 'Open',      color: 'success'  },
  closed:    { label: 'Closed',    color: 'warning'  },
  completed: { label: 'Completed', color: 'primary'  },
};
const APP_WORKFLOW = [
  'submitted','under_review','shortlisted','interview_scheduled','interviewed','accepted'
];
const fmtD  = d => d ? new Date(d).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' }) : '—';
const fmtDT = d => d ? new Date(d).toLocaleString('en-GB', { day:'numeric', month:'short', hour:'2-digit', minute:'2-digit' }) : '—';
const ptLabel = v => (v||'').replace(/_/g,' ').replace(/\b\w/g,l=>l.toUpperCase());

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

// ── Intake Form Dialog ─────────────────────────────────────────────────────────
function IntakeDialog({ open, onClose, onSave, editing, academicYears }) {
  const today = new Date().toISOString().split('T')[0];
  const BLANK = { title:'', academic_year_id:'', grade_applying:'', open_date:today,
    close_date:'', interview_start:'', interview_end:'', max_applicants:'',
    description:'', requirements:'', status:'draft' };
  const [form, setForm] = useState(BLANK);
  const setF = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (!open) return;
    if (editing) setForm({ ...BLANK, ...editing, academic_year_id: editing.academic_year_id||'',
      open_date: editing.open_date?.split('T')[0]||today,
      close_date: editing.close_date?.split('T')[0]||'',
      interview_start: editing.interview_start?.split('T')[0]||'',
      interview_end: editing.interview_end?.split('T')[0]||'' });
    else { const cur = academicYears.find(y => y.is_current); setForm({...BLANK, academic_year_id: cur?.id||''}); }
  }, [open, editing]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ bgcolor:'#0f2b4a', color:'white', display:'flex', alignItems:'center', gap:1 }}>
        <HowToReg /> {editing ? 'Edit Intake' : 'Open New Intake'}
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} sx={{ pt:1 }}>
          <Grid item xs={12}><TextField fullWidth size="small" label="Intake Title *" value={form.title} onChange={setF('title')} placeholder="e.g. Grade 8 Intake 2026" /></Grid>
          <Grid item xs={6} sm={4}>
            <TextField select fullWidth size="small" label="Academic Year" value={form.academic_year_id} onChange={setF('academic_year_id')}>
              <MenuItem value="">None</MenuItem>
              {academicYears.map(y => <MenuItem key={y.id} value={y.id}>{y.year_label} — {y.term}{y.is_current?' ✓':''}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={4}><TextField fullWidth size="small" label="Grade Applying For" value={form.grade_applying} onChange={setF('grade_applying')} placeholder="e.g. Grade 8, Form 1" /></Grid>
          <Grid item xs={6} sm={4}><TextField fullWidth size="small" type="number" label="Max Applicants" value={form.max_applicants} onChange={setF('max_applicants')} placeholder="Leave blank = unlimited" /></Grid>
          <Grid item xs={6} sm={3}><TextField fullWidth size="small" type="date" label="Opens *" value={form.open_date} onChange={setF('open_date')} InputLabelProps={{ shrink:true }} /></Grid>
          <Grid item xs={6} sm={3}><TextField fullWidth size="small" type="date" label="Closes *" value={form.close_date} onChange={setF('close_date')} InputLabelProps={{ shrink:true }} /></Grid>
          <Grid item xs={6} sm={3}><TextField fullWidth size="small" type="date" label="Interview Start" value={form.interview_start} onChange={setF('interview_start')} InputLabelProps={{ shrink:true }} /></Grid>
          <Grid item xs={6} sm={3}><TextField fullWidth size="small" type="date" label="Interview End" value={form.interview_end} onChange={setF('interview_end')} InputLabelProps={{ shrink:true }} /></Grid>
          <Grid item xs={12}>
            <TextField select fullWidth size="small" label="Status" value={form.status} onChange={setF('status')}>
              {Object.entries(INTAKE_STATUS).map(([k,v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12}><TextField fullWidth size="small" label="Description / About this Intake" value={form.description} onChange={setF('description')} multiline rows={3} placeholder="Describe what this intake is for, selection criteria, etc." /></Grid>
          <Grid item xs={12}><TextField fullWidth size="small" label="Required Documents" value={form.requirements} onChange={setF('requirements')} multiline rows={3} placeholder="List all documents applicants must upload. e.g.&#10;• Birth Certificate&#10;• Previous School Report&#10;• ID Document" /></Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSave(form)} sx={{ bgcolor:'#0f2b4a' }}>{editing ? 'Update' : 'Create Intake'}</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Application Review Dialog ──────────────────────────────────────────────────
function ReviewDialog({ open, onClose, appId, onUpdated }) {
  const { enqueueSnackbar } = useSnackbar();
  const [app, setApp]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [form, setForm] = useState({
    status:'', review_notes:'', interview_date:'', interview_venue:'',
    interview_notes:'', interview_score:'', decision_notes:''
  });

  useEffect(() => {
    if (!open || !appId) return;
    setLoading(true);
    api.get(`/admissions/applications/${appId}`)
      .then(r => { if (r.data.success) { setApp(r.data.data); setForm(f => ({ ...f, status: r.data.data.status, review_notes: r.data.data.review_notes||'', interview_date: r.data.data.interview_date ? r.data.data.interview_date.slice(0,16) : '', interview_venue: r.data.data.interview_venue||'', interview_notes: r.data.data.interview_notes||'', interview_score: r.data.data.interview_score||'', decision_notes: r.data.data.decision_notes||'' })); } })
      .finally(() => setLoading(false));
  }, [open, appId]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admissions/applications/${appId}/status`, form);
      enqueueSnackbar('Application updated', { variant:'success' });
      onUpdated(); onClose();
    } catch (err) { enqueueSnackbar(err.response?.data?.message || 'Error', { variant:'error' }); }
    finally { setSaving(false); }
  };

  const verifyDoc = async (docId) => {
    try {
      await api.put(`/admissions/applications/doc/${docId}/verify`);
      enqueueSnackbar('Document verified', { variant:'success' });
      const r = await api.get(`/admissions/applications/${appId}`);
      if (r.data.success) setApp(r.data.data);
    } catch { enqueueSnackbar('Error', { variant:'error' }); }
  };

  const stepIdx = APP_WORKFLOW.indexOf(form.status);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ bgcolor:'#0f2b4a', color:'white' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography fontWeight={700}>{app ? `${app.candidate_first_name} ${app.candidate_last_name}` : 'Loading…'}</Typography>
            <Typography fontSize={12} sx={{ opacity:0.7 }}>{app?.intake_title} · {app?.grade_applying}</Typography>
          </Box>
          {app && <Chip label={STATUS_CFG[app.status]?.label} color={STATUS_CFG[app.status]?.color} size="small" sx={{ fontWeight:700 }} />}
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ p:0 }}>
        {loading ? <Box sx={{ p:4, textAlign:'center' }}><CircularProgress /></Box> : app && (
          <Grid container sx={{ height:'100%' }}>
            {/* Left: application details */}
            <Grid item xs={12} md={7} sx={{ p:2.5, borderRight:'1px solid #f0f0f0' }}>
              {/* Workflow stepper */}
              <Stepper activeStep={stepIdx} alternativeLabel sx={{ mb:2.5 }}>
                {APP_WORKFLOW.map(s => <Step key={s}><StepLabel sx={{ fontSize:10 }}>{ptLabel(s)}</StepLabel></Step>)}
              </Stepper>

              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMore />}><Typography fontWeight={700} fontSize={13}>Candidate Details</Typography></AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={1}>
                    {[['Date of Birth', fmtD(app.candidate_dob)],['Gender', ptLabel(app.candidate_gender)],['Nationality', app.candidate_nationality||'—'],['Previous School', app.previous_school||'—'],['Previous Grade', app.previous_grade||'—']].map(([l,v]) => (
                      <Grid item xs={6} key={l}><Typography fontSize={11} color="text.secondary">{l}</Typography><Typography fontSize={13}>{v}</Typography></Grid>
                    ))}
                  </Grid>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}><Typography fontWeight={700} fontSize={13}>Parent / Guardian</Typography></AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={1}>
                    {[['Name', app.guardian_name||'—'],['Relationship', app.guardian_relationship||'—'],['Phone', app.guardian_phone||'—'],['Email', app.guardian_email||'—']].map(([l,v]) => (
                      <Grid item xs={6} key={l}><Typography fontSize={11} color="text.secondary">{l}</Typography><Typography fontSize={13}>{v}</Typography></Grid>
                    ))}
                  </Grid>
                </AccordionDetails>
              </Accordion>

              {app.motivation_statement && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMore />}><Typography fontWeight={700} fontSize={13}>Motivation Statement</Typography></AccordionSummary>
                  <AccordionDetails>
                    <Typography fontSize={13} sx={{ whiteSpace:'pre-wrap' }}>{app.motivation_statement}</Typography>
                  </AccordionDetails>
                </Accordion>
              )}

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography fontWeight={700} fontSize={13}>Supporting Documents ({app.documents?.length || 0})</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {!app.documents?.length ? <Typography fontSize={12} color="text.secondary">No documents uploaded yet</Typography> :
                    app.documents.map(doc => (
                      <Stack key={doc.id} direction="row" justifyContent="space-between" alignItems="center" sx={{ py:0.6, borderBottom:'1px solid #f5f5f5' }}>
                        <Box>
                          <Typography fontSize={12} fontWeight={600}>{doc.doc_type}</Typography>
                          <Typography fontSize={10} color="text.secondary">{doc.file_name} · {(doc.file_size/1024).toFixed(1)} KB · {fmtD(doc.uploaded_at)}</Typography>
                        </Box>
                        <Stack direction="row" spacing={0.5}>
                          {doc.verified ? <Chip label="Verified" size="small" color="success" icon={<Verified sx={{ fontSize:12 }} />} sx={{ fontSize:10 }} /> :
                            <Button size="small" variant="outlined" color="success" onClick={() => verifyDoc(doc.id)} sx={{ fontSize:10, py:0.3 }}>Verify</Button>}
                          <Tooltip title="Download">
                            <IconButton size="small" onClick={() => { const token = localStorage.getItem('sms_token'); window.open(`${API_BASE}/uploads/applications/${doc.file_path}?token=${token}`, '_blank'); }}>
                              <Download fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Stack>
                    ))
                  }
                </AccordionDetails>
              </Accordion>
            </Grid>

            {/* Right: review panel */}
            <Grid item xs={12} md={5} sx={{ p:2.5, bgcolor:'#fafafa' }}>
              <Typography fontWeight={700} gutterBottom>Review & Decision</Typography>
              <Stack spacing={2}>
                <TextField select fullWidth size="small" label="Update Status" value={form.status} onChange={e => setForm(f => ({ ...f, status:e.target.value }))}>
                  {Object.entries(STATUS_CFG).filter(([k]) => k !== 'draft' && k !== 'withdrawn').map(([k,v]) => (
                    <MenuItem key={k} value={k}>{v.label}</MenuItem>
                  ))}
                </TextField>
                <TextField fullWidth size="small" label="Review Notes" value={form.review_notes} onChange={e => setForm(f => ({ ...f, review_notes:e.target.value }))} multiline rows={2} placeholder="Internal notes about this application" />
                <Divider />
                <Typography fontSize={12} fontWeight={700} color="text.secondary">INTERVIEW</Typography>
                <TextField fullWidth size="small" type="datetime-local" label="Interview Date & Time" value={form.interview_date} onChange={e => setForm(f => ({ ...f, interview_date:e.target.value }))} InputLabelProps={{ shrink:true }} />
                <TextField fullWidth size="small" label="Venue / Location" value={form.interview_venue} onChange={e => setForm(f => ({ ...f, interview_venue:e.target.value }))} placeholder="e.g. Principal's Office, Room 3" />
                <Stack direction="row" spacing={1}>
                  <TextField fullWidth size="small" type="number" label="Interview Score (0-100)" value={form.interview_score} onChange={e => setForm(f => ({ ...f, interview_score:e.target.value }))} inputProps={{ min:0, max:100 }} />
                </Stack>
                <TextField fullWidth size="small" label="Interview Notes" value={form.interview_notes} onChange={e => setForm(f => ({ ...f, interview_notes:e.target.value }))} multiline rows={2} />
                <Divider />
                <Typography fontSize={12} fontWeight={700} color="text.secondary">DECISION</Typography>
                <TextField fullWidth size="small" label="Decision Notes (visible to applicant)" value={form.decision_notes} onChange={e => setForm(f => ({ ...f, decision_notes:e.target.value }))} multiline rows={2} placeholder="Message to applicant about the decision" />

                <Stack direction="row" spacing={1}>
                  <Button fullWidth variant="contained" color="success" onClick={() => { setForm(f => ({...f, status:'accepted'})); }} sx={{ fontSize:12 }}>Accept</Button>
                  <Button fullWidth variant="contained" color="error" onClick={() => { setForm(f => ({...f, status:'rejected'})); }} sx={{ fontSize:12 }}>Reject</Button>
                </Stack>
              </Stack>
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Typography variant="caption" color="text.secondary" sx={{ flexGrow:1, pl:1 }}>
          Submitted: {fmtDT(app?.submitted_at)}
          {app?.applicant_email && ` · ${app.applicant_email}`}
        </Typography>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" onClick={save} disabled={saving} sx={{ bgcolor:'#0f2b4a' }}>
          {saving ? 'Saving…' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Overview Tab ───────────────────────────────────────────────────────────────
function OverviewTab({ academicYears }) {
  const { enqueueSnackbar } = useSnackbar();
  const [stats, setStats]   = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/admissions/stats')
      .then(r => { if (r.data.success) setStats(r.data.data); })
      .catch(() => enqueueSnackbar('Failed to load', { variant:'error' }))
      .finally(() => setLoading(false));
  }, [enqueueSnackbar]);

  if (loading) return <CircularProgress />;
  return (
    <Grid container spacing={2}>
      {!stats.length && <Grid item xs={12}><Paper variant="outlined" sx={{ p:5, textAlign:'center', borderRadius:2 }}>
        <HowToReg sx={{ fontSize:56, color:'text.disabled', mb:1 }} />
        <Typography color="text.secondary">No intakes yet. Create your first intake to start accepting applications.</Typography>
      </Paper></Grid>}
      {stats.map(intake => {
        const total = intake.total || 0;
        const accepted = intake.accepted || 0;
        const pipe = [
          { label:'New',        val: intake.submitted||0,          color:'#1565c0' },
          { label:'Reviewing',  val: intake.under_review||0,       color:'#e65100' },
          { label:'Shortlisted',val: intake.shortlisted||0,        color:'#6a1b9a' },
          { label:'Interview',  val: intake.interview_scheduled||0, color:'#ad1457' },
          { label:'Accepted',   val: accepted,                     color:'#2e7d32' },
          { label:'Rejected',   val: intake.rejected||0,           color:'#c62828' },
        ];
        return (
          <Grid item xs={12} md={6} key={intake.id}>
            <Card variant="outlined" sx={{ borderRadius:2 }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb:1.5 }}>
                  <Box>
                    <Typography fontWeight={700} fontSize={15}>{intake.title}</Typography>
                    <Typography fontSize={11} color="text.secondary">{intake.grade_applying} · Closes {fmtD(intake.close_date)}</Typography>
                  </Box>
                  <Chip label={INTAKE_STATUS[intake.status]?.label} color={INTAKE_STATUS[intake.status]?.color} size="small" sx={{ fontWeight:700 }} />
                </Stack>
                <Grid container spacing={1} sx={{ mb:1.5 }}>
                  {pipe.map(p => (
                    <Grid item key={p.label}>
                      <Paper variant="outlined" sx={{ px:1.5, py:0.8, textAlign:'center', borderRadius:2 }}>
                        <Typography fontSize={18} fontWeight={800} sx={{ color:p.color }}>{p.val}</Typography>
                        <Typography fontSize={9} color="text.secondary">{p.label}</Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
                {total > 0 && intake.max_applicants && (
                  <Box>
                    <Box sx={{ display:'flex', justifyContent:'space-between', mb:0.3 }}>
                      <Typography fontSize={10} color="text.secondary">Capacity</Typography>
                      <Typography fontSize={10}>{total}/{intake.max_applicants}</Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={Math.min(total/intake.max_applicants*100,100)} sx={{ height:5, borderRadius:3 }} />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
}

// ── Applications Tab ───────────────────────────────────────────────────────────
function ApplicationsTab() {
  const { enqueueSnackbar } = useSnackbar();
  const [apps, setApps]         = useState([]);
  const [intakes, setIntakes]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [filterIntake, setFI]   = useState('');
  const [filterStatus, setFS]   = useState('');
  const [search, setSearch]     = useState('');
  const [reviewId, setReviewId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ar, ir] = await Promise.all([
        api.get('/admissions/applications', { params: { intake_id: filterIntake||undefined, status: filterStatus||undefined } }),
        api.get('/admissions/intakes')
      ]);
      if (ar.data.success) setApps(ar.data.data);
      if (ir.data.success) setIntakes(ir.data.data);
    } catch { enqueueSnackbar('Failed to load', { variant:'error' }); }
    finally { setLoading(false); }
  }, [filterIntake, filterStatus, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  const filtered = apps.filter(a => !search ||
    `${a.candidate_first_name} ${a.candidate_last_name} ${a.applicant_email}`.toLowerCase().includes(search.toLowerCase())
  );

  const newCount = apps.filter(a => a.status === 'submitted').length;

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb:2 }} flexWrap="wrap" alignItems="center">
        <TextField select size="small" label="Intake" value={filterIntake} onChange={e => setFI(e.target.value)} sx={{ minWidth:200 }}>
          <MenuItem value="">All intakes</MenuItem>
          {intakes.map(i => <MenuItem key={i.id} value={i.id}>{i.title}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Status" value={filterStatus} onChange={e => setFS(e.target.value)} sx={{ width:180 }}>
          <MenuItem value="">All</MenuItem>
          {Object.entries(STATUS_CFG).map(([k,v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
        </TextField>
        <TextField size="small" placeholder="Search name or email…" value={search} onChange={e => setSearch(e.target.value)}
          sx={{ minWidth:200 }} InputProps={{ startAdornment:<InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
        {newCount > 0 && <Chip label={`${newCount} new`} color="info" size="small" sx={{ fontWeight:700 }} />}
        <Box sx={{ flexGrow:1 }} />
        <Tooltip title="Refresh"><IconButton size="small" onClick={load}><Refresh /></IconButton></Tooltip>
        <Button size="small" variant="outlined" startIcon={<FileDownload />} onClick={() =>
          exportCSV('applications', ['Name','Email','Intake','Grade','Status','Submitted','Documents'],
            filtered.map(a => [`${a.candidate_first_name} ${a.candidate_last_name}`, a.applicant_email, a.intake_title, a.grade_applying, a.status, fmtD(a.submitted_at), a.doc_count||0]))
        }>Export</Button>
      </Stack>

      {loading ? <CircularProgress /> : (
        <Card variant="outlined" sx={{ borderRadius:2 }}>
          <TableContainer sx={{ maxHeight:520 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {['Candidate','Email / Phone','Intake','Status','Docs','Submitted','Interview','Actions'].map(h => (
                    <TableCell key={h} sx={{ bgcolor:'#0f2b4a', color:'white', fontWeight:700, fontSize:11 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 && <TableRow><TableCell colSpan={8} align="center" sx={{ py:4, color:'text.secondary' }}>No applications found</TableCell></TableRow>}
                {filtered.map((a, i) => (
                  <TableRow key={a.id} hover sx={{ bgcolor: a.status==='submitted'?'#fffde7': i%2===0?'white':'#fafafa', cursor:'pointer' }} onClick={() => setReviewId(a.id)}>
                    <TableCell>
                      <Typography fontSize={12} fontWeight={600}>{a.candidate_last_name}, {a.candidate_first_name}</Typography>
                      <Typography fontSize={10} color="text.secondary">{a.grade_applying}</Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize:11 }}>{a.applicant_email}<br/><Typography component="span" fontSize={10} color="text.secondary">{a.applicant_phone}</Typography></TableCell>
                    <TableCell sx={{ fontSize:11, maxWidth:140 }} noWrap>{a.intake_title}</TableCell>
                    <TableCell><Chip label={STATUS_CFG[a.status]?.label} color={STATUS_CFG[a.status]?.color} size="small" sx={{ fontSize:10, fontWeight:700 }} /></TableCell>
                    <TableCell align="center"><Chip label={a.doc_count||0} size="small" color={a.doc_count>0?'success':'default'} sx={{ fontSize:10 }} /></TableCell>
                    <TableCell sx={{ fontSize:11, color:'text.secondary' }}>{fmtD(a.submitted_at)}</TableCell>
                    <TableCell sx={{ fontSize:11, color:'text.secondary' }}>{a.interview_date ? fmtD(a.interview_date) : '—'}</TableCell>
                    <TableCell onClick={e => e.stopPropagation()}>
                      <Tooltip title="Review">
                        <IconButton size="small" color="primary" onClick={() => setReviewId(a.id)}><Assignment fontSize="small" /></IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}
      <ReviewDialog open={!!reviewId} appId={reviewId} onClose={() => setReviewId(null)} onUpdated={load} />
    </Box>
  );
}

// ── Intakes Tab ────────────────────────────────────────────────────────────────
function IntakesTab({ academicYears }) {
  const { enqueueSnackbar } = useSnackbar();
  const [intakes, setIntakes]  = useState([]);
  const [loading, setLoading]  = useState(false);
  const [dialog, setDialog]    = useState(false);
  const [editing, setEditing]  = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/admissions/intakes'); if (r.data.success) setIntakes(r.data.data); }
    catch { enqueueSnackbar('Failed to load', { variant:'error' }); }
    finally { setLoading(false); }
  }, [enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  const save = async (form) => {
    try {
      if (editing) { await api.put(`/admissions/intakes/${editing.id}`, form); enqueueSnackbar('Updated', { variant:'success' }); }
      else { await api.post('/admissions/intakes', form); enqueueSnackbar('Intake created', { variant:'success' }); }
      setDialog(false); setEditing(null); load();
    } catch (err) { enqueueSnackbar(err.response?.data?.message||'Error', { variant:'error' }); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this intake?')) return;
    try { await api.delete(`/admissions/intakes/${id}`); enqueueSnackbar('Deleted', { variant:'success' }); load(); }
    catch (err) { enqueueSnackbar(err.response?.data?.message||'Error', { variant:'error' }); }
  };

  return (
    <Box>
      <Stack direction="row" sx={{ mb:2 }} justifyContent="flex-end">
        <Button variant="contained" startIcon={<Add />} onClick={() => { setEditing(null); setDialog(true); }} sx={{ bgcolor:'#0f2b4a' }}>
          Open New Intake
        </Button>
      </Stack>
      {loading ? <CircularProgress /> : (
        <Grid container spacing={2}>
          {intakes.map(intake => (
            <Grid item xs={12} md={6} key={intake.id}>
              <Card variant="outlined" sx={{ borderRadius:2 }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box sx={{ flexGrow:1, mr:1 }}>
                      <Typography fontWeight={700} fontSize={15}>{intake.title}</Typography>
                      <Typography fontSize={11} color="text.secondary">{intake.grade_applying} · Year: {intake.year_label||'—'}</Typography>
                    </Box>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Chip label={INTAKE_STATUS[intake.status]?.label} color={INTAKE_STATUS[intake.status]?.color} size="small" sx={{ fontWeight:700 }} />
                      <IconButton size="small" onClick={() => { setEditing(intake); setDialog(true); }}><Edit fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" onClick={() => del(intake.id)}><Delete fontSize="small" /></IconButton>
                    </Stack>
                  </Stack>
                  <Divider sx={{ my:1 }} />
                  <Grid container spacing={1}>
                    <Grid item xs={6}><Typography fontSize={10} color="text.secondary">Opens</Typography><Typography fontSize={12}>{fmtD(intake.open_date)}</Typography></Grid>
                    <Grid item xs={6}><Typography fontSize={10} color="text.secondary">Closes</Typography><Typography fontSize={12}>{fmtD(intake.close_date)}</Typography></Grid>
                    {intake.interview_start && <Grid item xs={6}><Typography fontSize={10} color="text.secondary">Interviews</Typography><Typography fontSize={12}>{fmtD(intake.interview_start)} – {fmtD(intake.interview_end)}</Typography></Grid>}
                    <Grid item xs={6}><Typography fontSize={10} color="text.secondary">Capacity</Typography><Typography fontSize={12}>{intake.max_applicants || 'Unlimited'}</Typography></Grid>
                  </Grid>
                  <Stack direction="row" spacing={1} sx={{ mt:1 }}>
                    {[['Total',intake.total_applications||0,'default'],['New',intake.new_apps||0,'info'],['Shortlisted',intake.shortlisted||0,'primary'],['Accepted',intake.accepted||0,'success']].map(([l,v,c]) => (
                      <Chip key={l} label={`${v} ${l}`} size="small" color={c} sx={{ fontSize:10 }} />
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
          {!intakes.length && <Grid item xs={12}><Paper variant="outlined" sx={{ p:5, textAlign:'center', borderRadius:2 }}><Typography color="text.secondary">No intakes yet.</Typography></Paper></Grid>}
        </Grid>
      )}
      <IntakeDialog open={dialog} onClose={() => { setDialog(false); setEditing(null); }} onSave={save} editing={editing} academicYears={academicYears} />
    </Box>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function AdmissionsPortal() {
  const [tab, setTab]          = useState(0);
  const [academicYears, setAY] = useState([]);
  useEffect(() => { settingsAPI.getAcademicYears().then(r => { if (r.data.success) setAY(r.data.data); }).catch(() => {}); }, []);
  return (
    <Box>
      <Box sx={{ mb:3 }}>
        <Typography variant="h5" fontWeight={700} sx={{ display:'flex', alignItems:'center', gap:1 }}>
          <HowToReg color="primary" /> Admissions Portal
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage intake calls · Review applications · Schedule interviews · Accept / reject
        </Typography>
      </Box>
      <Alert severity="info" sx={{ mb:2, fontSize:12 }}>
        Parents apply via the public portal at <strong>/apply</strong>. Set an intake to <strong>Open</strong> to start receiving applications. All document uploads and status updates are visible to applicants in real time.
      </Alert>
      <Tabs value={tab} onChange={(_,v) => setTab(v)} sx={{ mb:1, borderBottom:1, borderColor:'divider' }}>
        <Tab icon={<BarChart fontSize="small" />}  label="Overview"    iconPosition="start" sx={{ fontSize:12 }} />
        <Tab icon={<EventNote fontSize="small" />} label="Intakes"     iconPosition="start" sx={{ fontSize:12 }} />
        <Tab icon={<Assignment fontSize="small" />} label="Applications" iconPosition="start" sx={{ fontSize:12 }} />
      </Tabs>
      <TabPanel value={tab} index={0}><OverviewTab academicYears={academicYears} /></TabPanel>
      <TabPanel value={tab} index={1}><IntakesTab academicYears={academicYears} /></TabPanel>
      <TabPanel value={tab} index={2}><ApplicationsTab /></TabPanel>
    </Box>
  );
}
